import { motion } from "framer-motion";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CustomOrder = () => {
    const [form, setForm] = useState({ name: "", email: "", idea: "", base_model: "White low-top", colorway: "Black & red" });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.idea) {
            toast.error("Fill in all fields");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/custom-requests`, form);
            setSent(true);
            setForm({ name: "", email: "", idea: "", base_model: "White low-top", colorway: "Black & red" });
        } catch {
            toast.error("Something went wrong — try again");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="custom" data-testid="custom-order-section" className="border-t border-vault-border">
            <div className="mx-auto grid max-w-7xl gap-16 px-6 py-28 md:py-40 lg:grid-cols-2">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                >
                    <p className="font-mono text-xs tracking-[0.3em] text-vault-red">CHAPTER 03</p>
                    <h2 className="mt-3 font-display text-5xl uppercase leading-none text-stone-900 md:text-7xl">
                        Your vision.
                        <br />
                        <span className="text-outline-red">Our cans.</span>
                    </h2>
                    <p className="mt-8 max-w-md text-sm leading-relaxed text-stone-600">
                        A club crest. A nickname. A memory. Send us the idea and we turn it into a pair nobody else on
                        the planet owns. Every custom commission starts with a sketch — you approve it before we spray.
                    </p>
                    <ul className="mt-10 space-y-4 font-mono text-xs tracking-[0.15em] text-stone-500">
                        <li className="flex gap-4">
                            <span className="text-vault-red">A.</span> SEND YOUR IDEA
                        </li>
                        <li className="flex gap-4">
                            <span className="text-vault-red">B.</span> APPROVE THE SKETCH
                        </li>
                        <li className="flex gap-4">
                            <span className="text-vault-red">C.</span> WE SPRAY, SEAL & SHIP
                        </li>
                    </ul>
                </motion.div>

                <motion.form
                    data-testid="custom-order-form"
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className="border border-vault-border bg-vault-black p-8 md:p-10"
                >
                    {sent ? (
                        <div data-testid="custom-request-confirmation" className="py-10 text-center">
                            <CheckCircle2 size={44} className="mx-auto text-vault-red" />
                            <h3 className="mt-6 font-display text-3xl uppercase text-stone-900">Request Received</h3>
                            <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-stone-600">
                                We&apos;ll come back to you in a moment. Keep an eye on your inbox — your price quote is
                                on its way by email.
                            </p>
                            <button
                                data-testid="custom-another-button"
                                onClick={() => setSent(false)}
                                className="mt-8 border border-vault-border px-6 py-3 font-mono text-xs tracking-[0.25em] text-stone-600 transition-colors hover:border-vault-red hover:text-stone-900"
                            >
                                SEND ANOTHER IDEA
                            </button>
                        </div>
                    ) : (
                        <>
                            <label className="mb-2 block font-mono text-[10px] tracking-[0.3em] text-stone-500">NAME</label>
                            <input
                                data-testid="custom-name-input"
                                value={form.name}
                                onChange={set("name")}
                                className="mb-6 w-full border border-vault-border bg-transparent px-4 py-3 text-sm text-stone-900 placeholder-stone-400"
                                placeholder="Your name"
                            />
                            <label className="mb-2 block font-mono text-[10px] tracking-[0.3em] text-stone-500">EMAIL</label>
                            <input
                                data-testid="custom-email-input"
                                type="email"
                                value={form.email}
                                onChange={set("email")}
                                className="mb-6 w-full border border-vault-border bg-transparent px-4 py-3 text-sm text-stone-900 placeholder-stone-400"
                                placeholder="you@email.com"
                            />
                            <label className="mb-2 block font-mono text-[10px] tracking-[0.3em] text-stone-500">BASE MODEL</label>
                            <select
                                data-testid="custom-base-select"
                                value={form.base_model}
                                onChange={set("base_model")}
                                className="mb-6 w-full border border-vault-border bg-transparent px-4 py-3 text-sm text-stone-900"
                            >
                                {["White low-top", "Black runner", "Turf cleat", "Firm-ground cleat", "Surprise me"].map((o) => (
                                    <option key={o} value={o}>
                                        {o}
                                    </option>
                                ))}
                            </select>
                            <label className="mb-2 block font-mono text-[10px] tracking-[0.3em] text-stone-500">COLOR DIRECTION</label>
                            <select
                                data-testid="custom-colorway-select"
                                value={form.colorway}
                                onChange={set("colorway")}
                                className="mb-6 w-full border border-vault-border bg-transparent px-4 py-3 text-sm text-stone-900"
                            >
                                {["Black & red", "White & blue", "Gold & neutral", "Your call — surprise me"].map((o) => (
                                    <option key={o} value={o}>
                                        {o}
                                    </option>
                                ))}
                            </select>
                            <label className="mb-2 block font-mono text-[10px] tracking-[0.3em] text-stone-500">YOUR IDEA</label>
                            <textarea
                                data-testid="custom-idea-input"
                                rows={5}
                                value={form.idea}
                                onChange={set("idea")}
                                className="mb-8 w-full resize-none border border-vault-border bg-transparent px-4 py-3 text-sm text-stone-900 placeholder-stone-400"
                                placeholder="Describe the design in your head — colors, motifs, the story behind it."
                            />
                            <button
                                data-testid="custom-submit-button"
                                type="submit"
                                disabled={sending}
                                className="btn-sweep flex w-full items-center justify-center gap-3 border border-vault-red py-4 font-mono text-sm tracking-[0.25em] text-stone-900 disabled:opacity-50"
                            >
                                <Send size={15} /> {sending ? "SENDING..." : "REQUEST CUSTOM DESIGN"}
                            </button>
                        </>
                    )}
                </motion.form>
            </div>
        </section>
    );
};
